import React, { Component } from 'react';

import Parser from 'html-react-parser';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Button,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  UncontrolledTooltip,
} from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import ShareModalLinkCopy from './ModalLinkCopy';
import ErrorBoundary from '../ErrorBoundary';
import UpgradePlanButton from '../UpgradePlanButton';

class ShareModalLinkSharing extends Component {
  constructor(props) {
    super(props);

    this.setLink = this.setLink.bind(this);
  }

  getLinkUser() {
    const { selectedUsers } = this.props;
    return selectedUsers.find(u => (u.type === 'link'));
  }

  getExpirationText() {
    const { intl, expirationDate } = this.props;
    if (expirationDate) {
      const days = expirationDate.diff(moment(), 'days') + 1;
      return `${intl.formatMessage({ defaultMessage: 'Expires in' })}
          <strong>${intl.formatMessage({ defaultMessage: '{days} {days, plural, one {day} other {days}}.' }, { days })}</strong>`;
    }
    return intl.formatMessage({ defaultMessage: 'Never expires.' });
  }

  getShareText() {
    const { linkRequireLogin } = this.props;
    if (linkRequireLogin) {
      return (
        <FormattedMessage
          defaultMessage="Share with anyone <strong>logged in</strong>."
          values={{ strong: (msg) => <strong>{msg}</strong> }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="Share with anyone."
        values={{ strong: (msg) => <strong>{msg}</strong> }}
      />
    );
  }

  getLink() {
    const user = this.getLinkUser();
    return `${window.location.protocol}//${window.location.host}/shares/${user.uuid}`;
  }

  getQRCodeLink() {
    const { links } = this.props;
    return links[0] ? links[0].qr_code_url : false;
  }

  getShortLink() {
    const { links } = this.props;
    return links[0] ? links[0].short_link_url : { prefix: '', suffix: '' };
  }

  setLink() {
    const { handleAdd } = this.props;
    handleAdd({
      uuid: uuidv4(),
      email: null,
      name: null,
      avatar: null,
      type: 'link',
    });
  }

  render() {
    const {
      intl,
      openSettings,
      changeLinkSuffix,
      errors,
      saving,
    } = this.props;

    if (!this.getShortLink().prefix) {
      return (
        <div className="share-link share-item">
          <div className="user-avatar share-avatar">
            <div className="avatar avatar-40 icon-avatar outline-avatar primary">
              <i className="fas fa-link" />
            </div>
          </div>
          <div className="share-item-body share-link-body">
            <h6 className="share-item-title">
              {intl.formatMessage({ defaultMessage: 'Share a link' })}
            </h6>
            <span className="share-item-details share-link-details">
              {intl.formatMessage({ defaultMessage: 'Create a link to share with anyone. Link not set.' })}
            </span>
          </div>
          <div className="share-item-actions share-link-actions">
            <Button color="link" disabled={saving} onClick={this.setLink}>
              {saving ? (
                intl.formatMessage({ defaultMessage: 'Wait...' })
              ) : (
                intl.formatMessage({ defaultMessage: 'Set a link' })
              )}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <ErrorBoundary>
        <div className="share-link">
          <div className="share-item">
            <div className="user-avatar share-avatar">
              <div className="avatar avatar-40 icon-avatar outline-avatar primary">
                <i className="fas fa-link" />
              </div>
            </div>
            <div className="share-item-body share-link-body">
              <h6 className="share-item-title">
                {intl.formatMessage({ defaultMessage: 'Share a link' })}
              </h6>
              <span className="share-item-details share-link-details">
                {this.getShareText()} {Parser(`${this.getExpirationText()}`)}
              </span>
            </div>
            <div className="share-link-open-settings-wrapper">
              <Button
                color="primary"
                id="linkOpenSettings"
                disabled={saving}
                onClick={openSettings}
              >
                <i className="far fa-cog" />
              </Button>
              <UncontrolledTooltip placement="top" target="linkOpenSettings">
                {saving ? (
                  intl.formatMessage({ defaultMessage: 'Please wait...' })
                ) : (
                  intl.formatMessage({ defaultMessage: 'Advanced share settings' })
                )}
              </UncontrolledTooltip>
              <UpgradePlanButton
                feature="share-link-settings"
                simple={true}
              />
            </div>
          </div>
          <Form className="share-link-form" onSubmit={e => e.preventDefault()}>
            <div className="share-link-wrapper">
              <div className="share-link-inputs-wrapper">
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>{this.getShortLink().prefix.replace('https://', '')}</InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="text"
                    id="inpShareLink"
                    name="shareLink"
                    className="share-link-suffix"
                    value={this.getShortLink().suffix}
                    onChange={changeLinkSuffix}
                    invalid={Boolean(errors.share_link_suffix)}
                  />
                </InputGroup>
                {!errors.share_link_suffix && (
                  <ShareModalLinkCopy linkUrl={`${this.getShortLink().prefix}${this.getShortLink().suffix}`} />
                )}
              </div>
              {this.getQRCodeLink() && (
                <>
                  <a href={this.getQRCodeLink()} id="linkQRCode" className="btn btn-link btn-link-qr-code" download={`share-${this.getLink().split('/').pop()}.png`}>
                    <i className="far fa-qrcode" />
                  </a>
                  <UncontrolledTooltip placement="bottom" target="linkQRCode">
                    {intl.formatMessage({ defaultMessage: 'Get QR Code' })}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {errors.share_link_suffix && (
              <FormFeedback style={{ display: 'block' }}>
                {errors.share_link_suffix}
              </FormFeedback>
            )}
          </Form>
        </div>
      </ErrorBoundary>
    );
  }
}

ShareModalLinkSharing.propTypes = {
  intl: PropTypes.object.isRequired,
  selectedUsers: PropTypes.array.isRequired,
  linkRequireLogin: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  expirationDate: PropTypes.object,
  links: PropTypes.array.isRequired,
  saving: PropTypes.bool.isRequired,
  handleAdd: PropTypes.func.isRequired,
  openSettings: PropTypes.func.isRequired,
  changeLinkSuffix: PropTypes.func.isRequired,
};

ShareModalLinkSharing.defaultProps = {
  expirationDate: null,
};

export default injectIntl(ShareModalLinkSharing);
